import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart as LineIcon, Plus, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NetWorthCard from "@/components/NetWorthCard";
import PrintPageButton from "@/components/PrintPageButton";
import RowActions from "@/components/RowActions";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const EMPTY = {
    name: "", kind: "Stock", broker: "", folio_no: "", units: "",
    invested_amount: "", current_value: "", currency: "INR",
    purchase_date: "", maturity_date: "", interest_rate: "",
    sip_amount: "", sip_frequency: "", owner: "Self", notes: "",
};

const KIND_TONE = {
    Stock: "bg-sky-50 text-sky-700 border-sky-200",
    "Mutual Fund": "bg-violet-50 text-violet-700 border-violet-200",
    SIP: "bg-teal-50 text-teal-700 border-teal-200",
    "Fixed Deposit": "bg-emerald-50 text-emerald-700 border-emerald-200",
    Gold: "bg-amber-50 text-amber-800 border-amber-200",
    Crypto: "bg-orange-50 text-orange-700 border-orange-200",
};
const toneFor = (k) => KIND_TONE[k] || "bg-stone-50 text-stone-600 border-stone-200";

export default function Investments() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ total_invested: 0, total_current: 0, total_gain: 0, total_gain_pct: 0, count: 0, by_kind: [] });
    const [kinds, setKinds] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const [l, s] = await Promise.all([api.get("/investments"), api.get("/investments/summary")]);
            setRows(l.data || []);
            setSummary(s.data || {});
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { api.get("/investments/meta").then((r) => setKinds(r.data?.kinds || [])); }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            ["units", "invested_amount", "current_value", "interest_rate", "sip_amount"].forEach((k) => {
                if (body[k] === "" || body[k] == null) delete body[k]; else body[k] = Number(body[k]);
            });
            ["purchase_date", "maturity_date"].forEach((k) => { if (!body[k]) delete body[k]; });
            if (editingId) {
                await api.patch(`/investments/${editingId}`, body);
                toast.success("Investment updated");
            } else {
                await api.post("/investments", body);
                toast.success("Investment added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            name: row.name || "", kind: row.kind || "Stock", broker: row.broker || "",
            folio_no: row.folio_no || "", units: row.units ?? "",
            invested_amount: row.invested_amount ?? "", current_value: row.current_value ?? "",
            currency: row.currency || "INR",
            purchase_date: (row.purchase_date || "").slice(0, 10),
            maturity_date: (row.maturity_date || "").slice(0, 10),
            interest_rate: row.interest_rate ?? "",
            sip_amount: row.sip_amount ?? "", sip_frequency: row.sip_frequency || "",
            owner: row.owner || "Self", notes: row.notes || "",
        });
        setOpen(true);
    };
    const openDuplicate = (row) => { openEdit(row); setEditingId(null); };

    const remove = async (id) => {
        if (!window.confirm("Archive this investment?")) return;
        try { await api.delete(`/investments/${id}`); toast.success("Archived"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const gainTone = summary.total_gain >= 0 ? "text-emerald-700" : "text-red-700";

    return (
        <div data-testid="investments-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><LineIcon size={12} /> Investment Layer</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Everything you&apos;ve set aside.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Stocks, mutual funds, SIPs, gold, FDs, PPF, EPF, NPS &mdash; manual entry today, brokerage
                        sync later. Enough to see your net worth alongside your liabilities.
                    </p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <PrintPageButton />
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="inv-add" onClick={openAdd}>
                                <Plus size={14} className="mr-1.5" /> Add investment
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inv-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">{editingId ? "Edit investment" : "New investment"}</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    Anything you own that&apos;s meant to grow &mdash; stocks, MFs, gold, FDs, EPF, NPS.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Name *</Label>
                                        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" placeholder="e.g. HDFC Nifty 50 Index Fund" data-testid="inv-name" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Kind</Label>
                                        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="inv-kind"><SelectValue /></SelectTrigger>
                                            <SelectContent>{kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Broker / Platform</Label>
                                        <Input value={f.broker} onChange={(e) => setF({ ...f, broker: e.target.value })} className="mt-1.5" placeholder="Zerodha / Kuvera / HDFC Bank" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Folio / Account no</Label>
                                        <Input value={f.folio_no} onChange={(e) => setF({ ...f, folio_no: e.target.value })} className="mt-1.5 font-mono-figure" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Units</Label>
                                        <Input type="number" step="0.0001" value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} className="mt-1.5 font-mono-figure" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Invested (₹) *</Label>
                                        <Input required type="number" step="0.01" value={f.invested_amount} onChange={(e) => setF({ ...f, invested_amount: e.target.value })} className="mt-1.5 font-mono-figure" data-testid="inv-invested" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Current value (₹)</Label>
                                        <Input type="number" step="0.01" value={f.current_value} onChange={(e) => setF({ ...f, current_value: e.target.value })} className="mt-1.5 font-mono-figure" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Purchase date</Label>
                                        <Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Maturity date (FD/RD/Bond)</Label>
                                        <Input type="date" value={f.maturity_date} onChange={(e) => setF({ ...f, maturity_date: e.target.value })} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Interest rate %</Label>
                                        <Input type="number" step="0.01" value={f.interest_rate} onChange={(e) => setF({ ...f, interest_rate: e.target.value })} className="mt-1.5 font-mono-figure" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Owner</Label>
                                        <Select value={f.owner} onValueChange={(v) => setF({ ...f, owner: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {["Self", "Spouse", "Child", "Parent", "Business", "Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Notes</Label>
                                        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="inv-submit">
                                    {busy ? "Saving…" : editingId ? "Save changes" : "Save investment"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            {/* Net worth roll-up at top */}
            <NetWorthCard />

            {/* Portfolio summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Invested</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900" data-testid="inv-total-invested">{fmtMoney(summary.total_invested)}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Current value</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{fmtMoney(summary.total_current)}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Gain / Loss</div>
                    <div className={`font-mono-figure text-3xl mt-1 flex items-center gap-1 ${gainTone}`}>
                        {summary.total_gain >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        {fmtMoney(Math.abs(summary.total_gain))}
                    </div>
                    <div className={`text-[11px] mt-1 ${gainTone}`}>{summary.total_gain_pct >= 0 ? "+" : ""}{summary.total_gain_pct}%</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Holdings</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{summary.count}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{(summary.by_kind || []).length} kind{(summary.by_kind || []).length === 1 ? "" : "s"}</div>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Kind</th>
                                <th>Broker</th>
                                <th className="text-right">Invested</th>
                                <th className="text-right">Current</th>
                                <th className="text-right">P&L</th>
                                <th>Owner</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const cur = r.current_value ?? r.invested_amount;
                                const gain = cur - (r.invested_amount || 0);
                                return (
                                    <tr key={r.id} data-testid={`inv-row-${r.id}`}>
                                        <td>
                                            <div className="font-medium text-stone-900">{r.name}</div>
                                            <div className="text-[11px] text-stone-500">
                                                {r.folio_no && <>Folio {r.folio_no}</>}
                                                {r.purchase_date && <> · {fmtDate(r.purchase_date)}</>}
                                            </div>
                                        </td>
                                        <td><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${toneFor(r.kind)}`}>{r.kind}</span></td>
                                        <td className="text-stone-600 text-[12px]">{r.broker || "—"}</td>
                                        <td className="text-right font-mono-figure">{fmtMoney(r.invested_amount)}</td>
                                        <td className="text-right font-mono-figure">{fmtMoney(cur)}</td>
                                        <td className={`text-right font-mono-figure ${gain >= 0 ? "text-emerald-700" : "text-red-700"}`}>{gain >= 0 ? "+" : ""}{fmtMoney(gain).replace("₹", "₹")}</td>
                                        <td className="text-stone-600 text-[12px]">{r.owner || "—"}</td>
                                        <td>
                                            {canEdit && (
                                                <RowActions
                                                    id={r.id}
                                                    testIdBase="inv"
                                                    onEdit={() => openEdit(r)}
                                                    onDuplicate={() => openDuplicate(r)}
                                                    onArchive={() => remove(r.id)}
                                                    canDelete={false}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-stone-500 py-14"><Minus size={16} className="mx-auto mb-2 text-stone-400" /> No investments logged yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
